import { useAppStore } from 'store/useAppStore';
import type { AppState, AppStateKeys, StateKeys } from 'store/useAppStore.types';
import { useShallow } from 'zustand/react/shallow';

/**
 * Custom hook to select state properties or actions from a specific slice of the store using shallow equality.
 *
 * @template T - The type of the selected state properties or actions.
 * @param {AppStateKeys} sliceKey - The key of the slice to select from.
 * @param {T[]} props - The array of keys to select from the specified slice. Special case for 'actions' to select all actions.
 * @returns {Pick<AppState[AppStateKeys], T[number]>} - The selected state properties or actions.
 *
 * @example
 * const { currentSection, currentStep } = useAppSelector('quoteStepper', ['currentSection', 'currentStep']);
 * const { actions } = useAppSelector('quoteStepper', ['actions']);
 */
export const useAppSelector = <
  Slice extends AppStateKeys,
  Props extends (StateKeys<AppState[Slice]> | 'actions')[],
>(
  sliceKey: Slice,
  props: Props,
): Pick<AppState[Slice], Props[number]> => {
  // Runtime check for unique keys
  const uniqueProps = Array.from(new Set(props));

  if (uniqueProps.length !== props.length) {
    const duplicateKeys = props.filter((key, index) => props.indexOf(key) !== index);

    throw new Error(`Duplicate keys found in the props array: [${duplicateKeys.join(', ')}]`);
  }

  return useAppStore(
    useShallow((state: AppState) => {
      const slice = state[sliceKey];
      const selectedState = {} as Pick<AppState[Slice], Props[number]>;

      uniqueProps.forEach((key) => {
        if (key === 'actions') {
          // Special case for 'actions'
          if ('actions' in slice) {
            Object.assign(selectedState, { actions: slice.actions });
          } else {
            console.warn(`'actions' property does not exist in slice '${String(sliceKey)}'.`);
          }
        } else if (key in slice) {
          // Handle selecting from state
          selectedState[key] = slice[key];
        } else {
          console.warn(`Key '${String(key)}' does not exist in slice '${String(sliceKey)}'.`);
        }
      });

      return selectedState;
    }),
  );
};
